import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

interface AnalyzeAllResult {
  enqueued: number;
  skipped: number;
  skippedAccountIds: string[];
  totalAccounts: number;
}

type Mode = 'pullNew' | 'reanalyze';

/**
 * Bulk action: queue an analyze job for every account. Skips accounts whose
 * most-recent job is already queued/running so repeated clicks don't stack
 * duplicate work. Progress shows up in the Recent analyses panel below.
 */
export function SyncAllAccountsButton() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('pullNew');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AnalyzeAllResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fn = httpsCallable<
        { pullNew?: boolean },
        AnalyzeAllResult
      >(functions, 'analyzeAllAccountsNow');
      const res = await fn({ pullNew: mode === 'pullNew' });
      setResult(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card flex items-center justify-between gap-4 p-4">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-800">
          Sync all accounts
        </h3>
        <p className="mt-0.5 text-xs text-slate-500">
          Queues a re-analysis job for every account in one click. Accounts
          whose latest job is still running are skipped.
        </p>
        {result && (
          <p className="mt-2 text-xs text-emerald-700">
            Queued <span className="font-semibold">{result.enqueued}</span> of{' '}
            {result.totalAccounts} accounts
            {result.skipped > 0 && (
              <span className="text-slate-500">
                {' '}
                · {result.skipped} skipped (already running)
              </span>
            )}
            . Watch the Recent analyses table below.
          </p>
        )}
        {error && (
          <p className="mt-2 text-xs text-rose-700">
            {error}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setError(null);
          setResult(null);
        }}
        disabled={busy}
        className="btn-primary whitespace-nowrap"
      >
        {busy ? 'Queueing…' : 'Sync all accounts'}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="card w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-base font-semibold text-slate-900">
              Sync every account?
            </h4>
            <p className="mt-1 text-sm text-slate-600">
              This queues a background job per account. Heavy work is throttled
              by Cloud Tasks (max 5 concurrent), so it may take a while to
              finish on large pipelines.
            </p>

            <div className="mt-4 space-y-2">
              <label className="flex items-start gap-2 rounded-md border border-slate-200 p-3 text-sm">
                <input
                  type="radio"
                  name="syncMode"
                  className="mt-0.5"
                  checked={mode === 'pullNew'}
                  onChange={() => setMode('pullNew')}
                />
                <span>
                  <span className="font-medium text-slate-800">
                    Pull new calls + re-analyze
                  </span>
                  <span className="block text-xs text-slate-500">
                    Pulls any new Gong calls first, then re-runs synthesis.
                    Most thorough; equivalent to the per-account "Pull new
                    calls" button.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 rounded-md border border-slate-200 p-3 text-sm">
                <input
                  type="radio"
                  name="syncMode"
                  className="mt-0.5"
                  checked={mode === 'reanalyze'}
                  onChange={() => setMode('reanalyze')}
                />
                <span>
                  <span className="font-medium text-slate-800">
                    Re-analyze only
                  </span>
                  <span className="block text-xs text-slate-500">
                    Uses cached call analyses; skips Gong fetch. Faster and
                    cheaper.
                  </span>
                </span>
              </label>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  await onConfirm();
                  setOpen(false);
                }}
                disabled={busy}
                className="btn-primary"
              >
                {busy ? 'Queueing…' : 'Queue jobs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
